import React, { useState, useEffect, useCallback } from "react";
import Loader from "../loader/Loader";
import { Button } from "@mui/material";
import MUIDataTable from "mui-datatables";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import axios from "axios";
import AddCategoryCode from "./Addcategorycode";
import {
  hideConfirmModal,
  showConfirmModal,
  showModal,
  storeCategoryCodeList,
} from "../../redux/counterSlice";
import {
  useTablePagination,
  useTableHeight,
} from "../../hooks/useTablePagination";
import { usePermission } from "../../hooks/usePermission";

const CategoryCode = () => {
  const dispatch = useDispatch();
  const { reset } = useForm();
  const permissions = usePermission("category_code");

  const {
    page,
    rowsPerPage,
    searchText,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
  } = useTablePagination("category_code", 10);
  const tableHeight = useTableHeight();

  const [loader, setLoader] = useState(false);
  const categoryCodeList = useSelector((state) => state.counter.categoryCodeList || []);
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const hasActionPermission = permissions?.edit || permissions?.delete;

  const loadInitialData = useCallback(async () => {
    try {
      setLoader(true);
      const response = await axios.get("/category_code");
      // API returns { error, message, data: [...] }
      dispatch(storeCategoryCodeList(response.data?.data || []));
    } catch (err) {
      toast.error(err?.message || "Failed to fetch category codes");
    } finally {
      setLoader(false);
      dispatch(hideConfirmModal({ msg: "" }));
    }
  }, [dispatch]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const deleteClickHandler = async (id) => {
    try {
      const response = await axios.delete(`/category_code/${id}`);
      if (response.data?.error) {
        let errDesc = response.data?.message || "Something went wrong";
        if (response.data?.data) {
          errDesc = Object.values(response.data.data).join("\n");
        }
        dispatch(showModal({ msg: errDesc, error: true }));
      } else {
        toast.success(response.data?.message || "Deleted successfully");
        loadInitialData();
        handleClose();
      }
    } catch (error) {
      dispatch(
        showModal({
          msg:
            error?.response?.data?.message ||
            error?.message ||
            "Server Error",
          error: true,
        })
      );
    } finally {
      dispatch(hideConfirmModal({ msg: "" }));
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
    reset();
  };

  const editClickHandler = (item) => {
    setSelectedRow({ ...item });
    setOpen(true);
  };

  const filteredList = categoryCodeList
    .filter((item) =>
      item.category_code?.toLowerCase().includes(searchText?.toLowerCase() || "")
    )
    .sort((a, b) => a.id - b.id);

  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        filter: false,
        customBodyRenderLite: (dataIndex) => <p>{dataIndex + 1}</p>,
      },
    },


    {
      name: "id",
      label: "Cat Code ID",
      options: {
        customBodyRenderLite: (dataIndex) =>
          filteredList[dataIndex]?.id || "-",
      },
    },
    {
      name: "category_code",
      label: "Category Code",
      options: {
        customBodyRenderLite: (dataIndex) =>
          filteredList[dataIndex]?.category_code || "-",
      },
    },
    ...(hasActionPermission
      ? [
          {
            name: "Actions",
            label: "Actions",
            options: {
              filter: false,
              customBodyRenderLite: (dataIndex) => {
                const item = filteredList[dataIndex];
                return (
                  <div className="actionBtns" style={{ gap: "8px" }}>
                    {permissions?.edit && (
                      <Button
                        aria-label="edit"
                        onClick={() => editClickHandler(item)}
                      >
                        <EditIcon fontSize="medium" color="secondary" />
                      </Button>
                    )}
                    {permissions?.delete && (
                      <Button
                        aria-label="delete"
                        onClick={() =>
                          dispatch(
                            showConfirmModal({
                              msg: "Delete",
                              action: () =>
                                deleteClickHandler(item.id)
                            })
                          )
                        }
                      >
                        <DeleteOutlineOutlinedIcon
                          sx={{ color: "red" }}
                          fontSize="medium"
                        />
                      </Button>
                    )}
                  </div>
                );
              },
            },
          },
        ]
      : []),
  ];

  const options = {
    selectableRows: "none",
    filter: false,
    download: false,
    viewColumns: true,
    print: false,
    rowsPerPage: rowsPerPage,
    page: page,
    fixedHeader: true,
    tableBodyHeight: tableHeight,
    onChangePage,
    onChangeRowsPerPage,
    onSearchChange,
    searchText: searchText,
    textLabels: {
      body: {
        noMatch: loader ? "Loading..." : "No data available",
      },
    },
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          <h4 className="text-dark">Category Code</h4>
          <MUIDataTable
            data={filteredList}
            columns={columns}
            options={options}
            title={
              permissions?.add ? (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setSelectedRow(null);
                    setOpen(true);
                  }}
                  startIcon={<AddIcon />}
                >
                  New Category Code
                </Button>
              ) : null
            }
          />
        </>
      )}

      <AddCategoryCode
        open={open}
        handleClose={handleClose}
        selectedRow={selectedRow}
        loadInitialData={loadInitialData}
      />
    </>
  );
};

export default CategoryCode;